import React, { useState, ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

interface CollapsibleSectionProps {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  headerContent?: ReactNode;
  headerAction?: ReactNode;
  titleAction?: ReactNode;
  headerRight?: ReactNode;
  hideHeaderContentWhenOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  children,
  defaultOpen = false,
  headerContent = null,
  headerAction = null,
  titleAction = null,
  headerRight = null,
  hideHeaderContentWhenOpen = false
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-300 dark:border-gray-700 overflow-hidden backdrop-blur-sm">
      <div className="w-full p-3 sm:p-4 md:p-6 flex items-center gap-2 sm:gap-3 md:gap-4">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center text-left hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-all duration-300 rounded-lg p-1 sm:p-2 -ml-1 sm:-ml-2 min-w-0"
        >
          <div className="flex items-center min-w-0">
            <div className="flex-shrink-0">
              {icon}
            </div>
            <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100 ml-1.5 sm:ml-2 truncate">{title}</h2>
          </div>
        </button>
        {titleAction && (
          <div className="flex-shrink-0">
            {titleAction}
          </div>
        )}
        {/* Spacer before content for centering */}
        {(headerContent || headerAction) && (
          <div className="flex-1" />
        )}
        {/*
          Show headerContent or headerAction based on state:
          - If only headerContent: always show it (unless hideHeaderContentWhenOpen)
          - If both: show headerContent when collapsed, headerAction when expanded
          - If only headerAction: show when expanded
        */}
        {headerContent && !headerAction && !(hideHeaderContentWhenOpen && isOpen) && (
          <div className="min-w-0">
            {headerContent}
          </div>
        )}
        {headerContent && headerAction && (
          <div className="min-w-0">
            {isOpen ? headerAction : headerContent}
          </div>
        )}
        {!headerContent && headerAction && isOpen && (
          <div className="min-w-0">
            {headerAction}
          </div>
        )}
        {/* Spacer to always push chevron to the far right */}
        <div className="flex-1" />
        {/* Content on the far right, before chevron */}
        {headerRight && (
          <div className="flex-shrink-0">
            {headerRight}
          </div>
        )}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex-shrink-0 p-1.5 sm:p-2 hover:bg-gray-200/50 dark:hover:bg-gray-700/50 transition-all duration-300 rounded-lg"
        >
          <ChevronRight
            className={`w-4 h-4 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-90' : ''}`}
          />
        </button>
      </div>

      <div className={`transition-all duration-300 ${isOpen ? 'opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <div className="px-6 pb-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;
